import includes from 'lodash/includes'
import noop from 'lodash/noop'
import React, {
  FC,
  useContext,
  useEffect,
  useState,
} from 'react'
import styled from 'styled-components'

import { Link } from '@Components/Link'
import { NavigationEvent } from '@Events/Navigation'
import { imageURL } from '@Globals/Cloudinary'
import { MetadataContext } from '@Globals/Metadata'
import { storageAvailable } from '@Globals/Storage'
import { flexCenterChildren, sectionInner } from '@Globals/Style/mixins'
import { useMediaQuery } from '@Globals/useMediaQuery'

const StickyTopBarWrapper = styled(Link)<{ imageUrl: string }>`
  display: block;
  background-color: ${({ theme }) => theme.colors.greyDark};
  background-size: cover;
  background-position: center center;
  background-image: url(${({ imageUrl }) => imageUrl});

  .inner {
    ${sectionInner}
    ${flexCenterChildren}
    height: 100%;
    min-height: 40px;
    margin: 0 auto;
    padding: 5px 10px;

    p {
      display: block;
      padding: 0;
      text-align: center;
      text-transform: uppercase;
      color: ${({ theme }) => theme.colors.white};
      font-weight: bold;
    }
  }
`

const LS_STICKY_TOP_BAR_DATA = 'stickyTopBarData'

interface StickyTopBarState {
  all_stores?: '0' | '1'
  desktopImage?: string
  destPageType?: string
  link?: string
  mobileImage?: string
  post_id?: string
  stores?: string
  text?: string
  timestamp?: number
}

interface StickyTopBarProps {
  onVisibilityChange?: () => void
}

export const StickyTopBar: FC<StickyTopBarProps> = (
  {
    onVisibilityChange = noop,
  }: StickyTopBarProps,
) => {
  const [stickyTopBar, setStickyTopBar] = useState<StickyTopBarState | false>(false)
  const { store } = useContext(MetadataContext)
  const storeID = store?.id
  const isDesktop = useMediaQuery('(min-width:480px)')

  useEffect(() => {
    if (!storageAvailable('localStorage')) {
      return
    }

    const dataString = window.localStorage.getItem(LS_STICKY_TOP_BAR_DATA)
    const json: StickyTopBarState = dataString && JSON.parse(dataString)
    if (!json) {
      return
    }

    if (json.timestamp) {
      // Check timestamp
      const now = new Date().getTime()
      const difference = now - json.timestamp
      const threshold = 1000 * 60 * 60 * 24 * 14

      if (difference > threshold) {
        window.localStorage.removeItem(LS_STICKY_TOP_BAR_DATA)
        return
      }
    }

    if (json.all_stores === '1') {
      setStickyTopBar(json)
      return
    }

    if (json.stores && storeID && includes(json.stores, storeID)) {
      setStickyTopBar(json)
      return
    }
  }, [storeID])

  useEffect(() => {
    onVisibilityChange()
  }, [onVisibilityChange, stickyTopBar])

  if (stickyTopBar === false) {
    return null
  }

  const bgImage = isDesktop ? stickyTopBar.desktopImage : stickyTopBar.mobileImage
  return (
    <StickyTopBarWrapper
      event={new NavigationEvent({
        attributes: {
          dest_page_type: stickyTopBar.destPageType || '',
          dest_page_type_id: stickyTopBar.post_id || '',
          source_id: 'sticky_top_bar',
        },
        eventName: 'nav_link_click',
      })}
      href={stickyTopBar.link}
      imageUrl={bgImage ? imageURL(bgImage) : ''}
    >
      <div className="inner">
        <p>{stickyTopBar.text}</p>
      </div>
    </StickyTopBarWrapper>
  )
}
